/**
 * CategoryBox — collapsible category panel wrapping an inner DragDropLayout.
 *
 *   - Editable title in layout mode; collapse/expand toggle outside it
 *   - Optional native category reorder via header drag-handle icon
 *   - Drop-zone highlight when a card is dragged over the category
 *   - Frozen inner/Paper height while dragging a card out of this category
 *   - Post-drop settle window keeps overflow visible during inner animations
 *   - Empty state renders a tall drop zone for first-card placement
 */
import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import {
	Box,
	Collapse,
	Divider,
	IconButton,
	InputBase,
	Paper,
	Tooltip,
	Typography,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import DragIndicator from '@mui/icons-material/DragIndicator';
import EditIcon from '@mui/icons-material/Edit';
import DragDropLayout from './DragDropLayout';
import MinimizeSVG from '../../assets/svgs/minimize.svg';
import PopupPlusSVG from '../../assets/svgs/popup-plus.svg';
import { getCardDisplayTitle } from '../../utils/getCardDisplayTitle';

const CategoryBox = ({
	id,
	title,
	cards,
	onLayoutChange,
	onDelete,
	onUpdateTitle,
	renderCard,
	isLayoutMode,
	loading,
	isMobile,
	
	innerTwoColumnDesktop = false,
	onCardDrop,
	isCardOver = false,
	showDropSuccessFlash = false,
	onMouseEnter,
	onMouseLeave,
	draggedCardId,
	onDragStart,
	onDragStop,
	dragSourceId,
	hoveredCategoryId,
	hoveredStandalone,
	isCategoryReorderDragging,
	isCategoryDropTarget,
	onCategoryReorderDragStart,
	onCategoryReorderDragEnd,
	onCategoryReorderDragOver,
	onCategoryReorderDrop,
	useNativeCategoryReorder = true,
	suppressCategoryInnerPersistRef,
	onInnerLayoutDrag,
	layoutScrollContainerRef,
	dynamicCards = [],
	isCollapsed,
	onToggleCollapse,
	
	onCardDropAtEdge,
	
	transformScale = 1,
}) => {
	const isEmpty = !cards || cards.length === 0;
	const titleInputRef = useRef(null);
	const [titleDraft, setTitleDraft] = useState(title || '');
	const [isEditingTitle, setIsEditingTitle] = useState(false);
	const [localShowDetails, setLocalShowDetails] = useState(true);
	
	const isControlledCollapseEarly =
		typeof isCollapsed === 'boolean' && typeof onToggleCollapse === 'function';
	const showDetails = isLayoutMode
		? true
		: isControlledCollapseEarly
			? !isCollapsed
			: localShowDetails;
	const collapseTimerRef = useRef(null);
	
	const [isPostDropSettling, setIsPostDropSettling] = useState(false);
	const postDropSettleTimerRef = useRef(null);
	const previousDraggedCardIdRef = useRef(null);

	const paperRef = useRef(null);
	const innerGridWrapperRef = useRef(null);
	const restingInnerHeightRef = useRef(null);
	const restingPaperHeightRef = useRef(null);
	const frozenInnerHeightRef = useRef(null);
	const frozenPaperHeightRef = useRef(null);
	const [frozenInnerHeight, setFrozenInnerHeight] = useState(null);
	const [frozenPaperHeight, setFrozenPaperHeight] = useState(null);
	const isDraggingFromThisCategory = Boolean(
		isLayoutMode && draggedCardId && dragSourceId === id
	);

	useLayoutEffect(() => {
		if (!isLayoutMode) return;
		if (isDraggingFromThisCategory) return;
		const innerEl = innerGridWrapperRef.current;
		if (innerEl) {
			const h = innerEl.getBoundingClientRect().height;
			
			if (h > 0 && (restingInnerHeightRef.current == null || h >= restingInnerHeightRef.current - 4)) {
				restingInnerHeightRef.current = h;
			}
		}
		const paperEl = paperRef.current;
		if (paperEl) {
			const ph = paperEl.getBoundingClientRect().height;
			if (ph > 0 && (restingPaperHeightRef.current == null || ph >= restingPaperHeightRef.current - 4)) {
				restingPaperHeightRef.current = ph;
			}
		}
	});

	useEffect(() => {
		if (!isLayoutMode) return undefined;
		if (isDraggingFromThisCategory) return undefined;
		if (typeof ResizeObserver === 'undefined') return undefined;
		const innerEl = innerGridWrapperRef.current;
		const paperEl = paperRef.current;
		const ro = new ResizeObserver((entries) => {
			for (const entry of entries) {
				const target = entry.target;
				const h = entry.contentRect?.height || target.getBoundingClientRect().height;
				if (!(h > 0)) continue;
				if (target === innerEl) restingInnerHeightRef.current = h;
				else if (target === paperEl) restingPaperHeightRef.current = h;
			}
		});
		if (innerEl) ro.observe(innerEl);
		if (paperEl) ro.observe(paperEl);
		return () => { try { ro.disconnect(); } catch {  } };
	}, [isLayoutMode, isDraggingFromThisCategory]);

	useLayoutEffect(() => {
		if (!isLayoutMode) return;
		if (isDraggingFromThisCategory) {
			if (frozenInnerHeightRef.current == null) {
				const tracked = restingInnerHeightRef.current;
				const live = innerGridWrapperRef.current
					? innerGridWrapperRef.current.getBoundingClientRect().height
					: 0;
				const h = Math.max(tracked || 0, live || 0);
				if (h > 0) {
					frozenInnerHeightRef.current = h;
					setFrozenInnerHeight(h);
				}
			}
			if (frozenPaperHeightRef.current == null) {
				const trackedP = restingPaperHeightRef.current;
				const liveP = paperRef.current
					? paperRef.current.getBoundingClientRect().height
					: 0;
				const ph = Math.max(trackedP || 0, liveP || 0);
				if (ph > 0) {
					frozenPaperHeightRef.current = ph;
					setFrozenPaperHeight(ph);
				}
			}
		} else {
			if (frozenInnerHeightRef.current != null) {
				frozenInnerHeightRef.current = null;
				setFrozenInnerHeight(null);
			}
			if (frozenPaperHeightRef.current != null) {
				frozenPaperHeightRef.current = null;
				setFrozenPaperHeight(null);
			}
		}
	}, [isLayoutMode, isDraggingFromThisCategory]);
	const isControlledCollapse = isControlledCollapseEarly;
	const handleToggleDetails = () => {
		if (isLayoutMode) return;
		if (isControlledCollapse) {
			
			onToggleCollapse(id);
			return;
		}
		setLocalShowDetails((prev) => !prev);
	};
	const showCardDropHover = Boolean(isLayoutMode && draggedCardId && isCardOver);
	const showReorderTarget = Boolean(isCategoryDropTarget);
	const highlightDropZone = showCardDropHover || showReorderTarget;
	const categoryCardNames = useMemo(
		() =>
			(cards || [])
				.map((card) => getCardDisplayTitle(card, { dynamicCards }))
				.filter(Boolean),
		[cards, dynamicCards]
	);

	useEffect(() => {
		if (!isEditingTitle) {
			setTitleDraft(title || '');
		}
	}, [title, isEditingTitle]);

	useEffect(() => {
		return () => {
			if (collapseTimerRef.current != null) {
				clearTimeout(collapseTimerRef.current);
				collapseTimerRef.current = null;
			}
			if (postDropSettleTimerRef.current != null) {
				clearTimeout(postDropSettleTimerRef.current);
				postDropSettleTimerRef.current = null;
			}
		};
	}, []);

	useEffect(() => {
		const prev = previousDraggedCardIdRef.current;
		previousDraggedCardIdRef.current = draggedCardId || null;
		if (!isLayoutMode) return;
		if (prev && !draggedCardId) {
			setIsPostDropSettling(true);
			if (postDropSettleTimerRef.current != null) {
				clearTimeout(postDropSettleTimerRef.current);
			}
			
			postDropSettleTimerRef.current = window.setTimeout(() => {
				setIsPostDropSettling(false);
				postDropSettleTimerRef.current = null;
			}, 2000);
		}
	}, [draggedCardId, isLayoutMode]);

	const commitTitleDraft = () => {
		const nextTitle = (titleDraft ?? '').trim();
		const currentTitle = (title ?? '').trim();
		if (nextTitle !== currentTitle) {
			onUpdateTitle(id, nextTitle);
		}
		setIsEditingTitle(false);
	};

	return (
		<Paper
			elevation={0}
			ref={paperRef}
			onMouseEnter={() => onMouseEnter && onMouseEnter(id)}
			onMouseLeave={() => onMouseLeave && onMouseLeave()}
			onMouseUp={() => {
				if (onCardDrop && draggedCardId) {
					onCardDrop(draggedCardId, id);
				}
			}}
			onDragOver={
				useNativeCategoryReorder
					? (e) => {
						if (!isLayoutMode || !onCategoryReorderDragOver) return;
						e.preventDefault();
						e.dataTransfer.dropEffect = 'move';
						onCategoryReorderDragOver(id);
					}
					: undefined
			}
			onDrop={
				useNativeCategoryReorder
					? (e) => {
						if (!isLayoutMode || !onCategoryReorderDrop) return;
						e.preventDefault();
						const fromId = e.dataTransfer.getData('application/x-hospital-category-id');
						if (fromId) {
							onCategoryReorderDrop(fromId, id);
						}
						onCategoryReorderDragEnd?.();
					}
					: undefined
			}
			sx={{
				...(useNativeCategoryReorder
					? { mb: isLayoutMode ? 5 : 4, ml: 1 }
					: {
						mb: isLayoutMode ? 1.25 : 0,
						ml: 0,
						height: 'fit-content',
						maxHeight: 'none',
						display: 'flex',
						flexDirection: 'column',
						overflow: isLayoutMode && showDetails ? 'visible' : 'hidden',
						boxSizing: 'border-box',
						flex: '0 0 auto',
					}),
				paddingLeft: isMobile ? 1 : 2,
				paddingRight: isMobile ? 1 : 2,
				paddingTop: showDetails ? (isMobile ? 1 : 1.75) : (isMobile ? 0.75 : 1),
				paddingBottom: showDetails
					? (isMobile ? 1 : (isLayoutMode ? 1.25 : (useNativeCategoryReorder ? 1.5 : 1.25)))
					: (isMobile ? 0.75 : 1),
				borderRadius: '14px',
				border: highlightDropZone ? '2px dashed' : '1px solid #cbd5e1',
				borderColor: highlightDropZone ? 'primary.main' : '#cbd5e1',
				backgroundColor: showCardDropHover
					? 'rgba(76, 118, 213, 0.08)'
					: '#eeeeee',
				position: 'relative',
				
				transition: isLayoutMode
					? 'border-color 0.2s ease, background-color 0.2s ease, box-shadow 0.2s ease'
					: 'border-color 0.2s ease, background-color 0.2s ease, box-shadow 0.2s ease, padding 1400ms cubic-bezier(0.4, 0, 0.2, 1)',
				
				boxShadow: showCardDropHover
					? 'none'
					: highlightDropZone
						? '0 4px 20px rgba(76, 118, 213, 0.18)'
						: '0 10px 15px -3px rgba(0, 0, 0, 0.04), 0 4px 6px -2px rgba(0, 0, 0, 0.02)',
				opacity: useNativeCategoryReorder && isCategoryReorderDragging ? 0.55 : 1,
				
				pointerEvents:
					isLayoutMode && isDraggingFromThisCategory ? 'none' : 'auto',
				
				...(isLayoutMode && isDraggingFromThisCategory && frozenPaperHeight
					? { minHeight: `${frozenPaperHeight}px` }
					: {}),
				...(showCardDropHover && {
					'@keyframes hospitalCategoryCardDropPulse': {
						'0%, 100%': { boxShadow: '0 4px 18px rgba(76, 118, 213, 0.2)' },
						'50%': { boxShadow: '0 6px 28px rgba(76, 118, 213, 0.32)' },
					},
					animation: 'hospitalCategoryCardDropPulse 1.2s ease-in-out infinite',
				}),
				...(showDropSuccessFlash && {
					'@keyframes hospitalCategoryDropSuccess': {
						'0%': {
							backgroundColor: 'rgba(76, 118, 213, 0.22)',
							boxShadow: '0 0 0 0 rgba(76, 118, 213, 0.45)',
						},
						'45%': {
							backgroundColor: 'rgba(76, 118, 213, 0.1)',
							boxShadow: '0 0 0 10px rgba(76, 118, 213, 0)',
						},
						'100%': { backgroundColor: '#f9fafb', boxShadow: '0 2px 4px rgba(0,0,0,0.02)' },
					},
					animation: 'hospitalCategoryDropSuccess 0.7s ease-out forwards',
				}),
			}}
			data-category-id={id}
			
			data-collapsed={String(!showDetails)}
		>
			<Box
				sx={{
					display: 'flex',
					alignItems: 'center',
					justifyContent: 'space-between',
					mb: showDetails ? (isMobile ? 1 : 1.5) : 0,
					ml: 0.5,
					flexShrink: 0,
					
					transition: isLayoutMode
						? 'none'
						: 'margin 1400ms cubic-bezier(0.4, 0, 0.2, 1)',
					
					...(isLayoutMode && !useNativeCategoryReorder && {
						cursor: 'grab',
						'&:active': { cursor: 'grabbing' },
					}),
				}}
				onMouseDown={(e) => {
					
					if (useNativeCategoryReorder) {
						e.stopPropagation();
					}
				}}
			>
				<Box sx={{ display: 'flex', alignItems: 'center', flex: 1 }}>
					{isLayoutMode && useNativeCategoryReorder && (
						<Tooltip title="Drag to reorder this category (moves all cards)" arrow>
							<span>
								<IconButton
									size="small"
									draggable
									onDragStart={(e) => {
										e.stopPropagation();
										e.dataTransfer.setData('application/x-hospital-category-id', id);
										e.dataTransfer.effectAllowed = 'move';
										onCategoryReorderDragStart?.(id);
									}}
									onDragEnd={() => onCategoryReorderDragEnd?.()}
									onMouseDown={(e) => e.stopPropagation()}
									sx={{
										cursor: 'grab',
										color: 'text.secondary',
										mr: 0.5,
										'&:active': { cursor: 'grabbing' },
									}}
									aria-label="Reorder category"
								>
									<DragIndicator fontSize="small" />
								</IconButton>
							</span>
						</Tooltip>
					)}
					{!isLayoutMode && (
						<Tooltip title={showDetails ? 'Minimize category' : 'Maximize category'} arrow>
							<IconButton
								size="small"
								onClick={handleToggleDetails}
								onMouseDown={(e) => e.stopPropagation()}
								sx={{ color: 'text.secondary', mr: 0.5 }}
								aria-label={showDetails ? 'Minimize category' : 'Maximize category'}
							>
								{showDetails ? <MinimizeSVG /> : <PopupPlusSVG />}
							</IconButton>
						</Tooltip>
					)}
					{isLayoutMode ? (
						<>
							<InputBase
								inputRef={titleInputRef}
								value={titleDraft}
								onChange={(e) => setTitleDraft(e.target.value)}
								onFocus={() => setIsEditingTitle(true)}
								onBlur={commitTitleDraft}
								onKeyDown={(e) => {
									e.stopPropagation();
									if (e.key === 'Enter') {
										e.preventDefault();
										titleInputRef.current?.blur();
									}
									if (e.key === 'Escape') {
										e.preventDefault();
										setTitleDraft(title || '');
										setIsEditingTitle(false);
										titleInputRef.current?.blur();
									}
								}}
								onMouseDown={(e) => e.stopPropagation()}
								placeholder="Category name"
								sx={{
									fontSize: '0.95rem',
									fontWeight: 700,
									color: '#4c76d5',
									letterSpacing: '0.3px',
									textTransform: 'capitalize',
									flex: '0 1 auto',
									minWidth: 0,
									'& .MuiInputBase-input': {
										padding: '4px 8px',
										border: '1px dashed transparent',
										'&:focus': {
											border: '1px dashed',
											borderColor: 'primary.main',
											backgroundColor: 'white',
										},
									},
								}}
							/>
							<Tooltip title="Edit category name" arrow>
								<IconButton
									size="small"
									onClick={() => titleInputRef.current?.focus()}
									onMouseDown={(e) => e.stopPropagation()}
									sx={{
										color: 'primary.main',
										ml: 0.25,
										flexShrink: 0,
									}}
									aria-label="Edit category name"
								>
									<EditIcon sx={{ fontSize: '1.1rem' }} />
								</IconButton>
							</Tooltip>
						</>
					) : (
						<Tooltip
							arrow
							placement="right-start"
							disableInteractive
							enterDelay={0}
							leaveDelay={0}
							TransitionProps={{ timeout: 0 }}
							disableHoverListener={isLayoutMode || categoryCardNames.length === 0}
							slotProps={{
								tooltip: {
									sx: {
										bgcolor: '#fff',
										color: 'text.primary',
										boxShadow: '0 8px 24px rgba(15, 23, 42, 0.14)',
										border: '1px solid #e2e8f0',
										maxWidth: 320,
									},
								},
								arrow: {
									sx: {
										color: '#fff',
										'&::before': {
											border: '1px solid #e2e8f0',
										},
									},
								},
							}}
							title={
								<Box sx={{ py: 0.25 }}>
									<Typography sx={{ fontSize: '0.78rem', fontWeight: 700, mb: 0.5 }}>
										Cards in this category
									</Typography>
									{categoryCardNames.map((cardName) => (
										<Typography key={cardName} sx={{ fontSize: '0.75rem', lineHeight: 1.35 }}>
											- {cardName}
										</Typography>
									))}
								</Box>
							}
						>
							<Typography
								variant="h6"
								onClick={handleToggleDetails}
								onMouseDown={(e) => e.stopPropagation()}
								sx={{
									fontWeight: 700,
									color: '#4c76d5',
									letterSpacing: '0.3px',
									fontSize: '0.95rem',
									textTransform: 'capitalize',
									cursor: 'pointer',
								}}
							>
								{titleDraft}
							</Typography>
						</Tooltip>
					)}
					<Divider sx={{ ml: 1.25, flexGrow: 1, borderColor: '#c5cbd3' }} />
				</Box>
				<Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
					{isLayoutMode && id !== 'cat_general' && (
						<IconButton
							size="small"
							onClick={() => onDelete(id)}
							onMouseDown={(e) => e.stopPropagation()}
							sx={{ color: 'error.main' }}
						>
							<DeleteIcon fontSize="small" />
						</IconButton>
					)}
				</Box>
			</Box>

			<Collapse
				in={showDetails}
				
				timeout={{ enter: 1400, exit: 1000 }}
				easing="cubic-bezier(0.4, 0, 0.2, 1)"
			>
				{isEmpty ? (
					<Box
						sx={{
							...(isLayoutMode && isDraggingFromThisCategory ? { pointerEvents: 'auto' } : {}),
							py: useNativeCategoryReorder ? 4 : 3,
							pb: useNativeCategoryReorder ? undefined : (isLayoutMode ? 1.5 : 3),
							
							flex: useNativeCategoryReorder ? '0 0 auto' : '1 1 auto',
							
							minHeight: useNativeCategoryReorder ? 260 : 200,
							display: 'flex',
							alignItems: 'center',
							justifyContent: 'center',
							
							borderRadius: 1,
							color: 'text.secondary',
							overflow: 'hidden',
							backgroundColor: 'transparent',
							width: '100%',
						}}
					>
						<Typography variant="body2">No cards in this category yet. Drag a card here.</Typography>
					</Box>
				) : (
					<Box
						ref={innerGridWrapperRef}
						sx={{
							...(isLayoutMode && isDraggingFromThisCategory ? { pointerEvents: 'auto' } : {}),
							px: isMobile ? 0.75 : 1.5,
							pb: isMobile ? 0.5 : (isLayoutMode ? 0.5 : 0.75),
							flex: useNativeCategoryReorder ? undefined : '0 0 auto',
							minHeight: useNativeCategoryReorder ? undefined : 0,
							maxWidth: '100%',
							
							...(isDraggingFromThisCategory && frozenInnerHeight
								? {
									
									minHeight: `${frozenInnerHeight}px`,
									height: `${frozenInnerHeight}px`,
									maxHeight: `${frozenInnerHeight}px`,
								}
								: {}),
							
							overflow:
								isLayoutMode &&
								!isDraggingFromThisCategory &&
								!isPostDropSettling
									? 'hidden'
									: 'visible',
							boxSizing: 'border-box',
						}}
					>
						<DragDropLayout
							cards={cards}
							layout={cards}
							onLayoutChange={(newCards) => onLayoutChange(id, newCards)}
							isLayoutMode={isLayoutMode}
							disabled={loading}
							isMobile={isMobile}
							innerTwoColumnDesktop={innerTwoColumnDesktop}
							strictOverlapReorder
							strictOverlapThreshold={0.62}
							disableContainerGutter={!useNativeCategoryReorder}
							layoutScrollContainerRef={layoutScrollContainerRef}
							transformScale={transformScale}
							containerCategoryId={id}
							enableInnerEdgeGaps={false}
							onCrossContainerEdgeDrop={(cardId, position, targetCategoryId) => {
								if (onCardDropAtEdge) onCardDropAtEdge(cardId, position, targetCategoryId);
							}}
							unlinkDragActive={Boolean(isLayoutMode && draggedCardId && dragSourceId === id)}
							onDragStart={(_layout, _oldItem, newItem) => onDragStart && onDragStart(newItem?.i, id)}
							onDrag={onInnerLayoutDrag}
							onDragStop={(_layout, _oldItem, newItem, _placeholder, e) =>
								onDragStop && onDragStop(newItem?.i, id, e)
							}
							shouldPersistLayoutAfterDrag={() => {
								if (suppressCategoryInnerPersistRef?.current) return false;
								if (!isLayoutMode || !draggedCardId) return true;
								if (hoveredStandalone) return false;
								if (hoveredCategoryId != null && hoveredCategoryId !== id) return false;
								return true;
							}}
						>
							{renderCard}
						</DragDropLayout>
					</Box>
				)}
			</Collapse>
		</Paper>
	);
};

export default CategoryBox;
